import { INTERCOM_LAUNCH_TARGET_CLASS } from 'lib/intercom/intercom';
import { getUserData } from 'approot/shared/api/auth/auth';

/**
 * The idle-reload timer and the Intercom, Google Tag Manager and Crazy Egg
 * loaders. These were four inline `<script>` blocks in index.html until
 * TT-1004 moved them into the bundle so `script-src` could drop
 * `'unsafe-inline'`. Nonces were not available: the Lambda@Edge origin-response
 * cannot read the S3 body to rewrite it, and per-environment `%REACT_APP_*%`
 * substitution rules out static hashes. Each `start*` below is one block.
 */

/**
 * Intercom's loader installs a callable stub that queues calls made before the
 * widget script arrives. `lib/intercom/intercom.tsx` guards on `window.Intercom`
 * being truthy, so the stub is what keeps those calls from silently no-opping.
 */
type IntercomStub = {
  (...args: unknown[]): void;
  q: unknown[][];
  c: (args: unknown) => void;
};

type BootstrapWindow = Window &
  typeof globalThis & {
    Intercom?: IntercomStub | ((...args: unknown[]) => void);
    intercomSettings?: Record<string, unknown>;
    dataLayer?: Record<string, unknown>[];
  };

const getBootstrapWindow = (): BootstrapWindow => window as BootstrapWindow;

/** Returns the element so callers can hook `onload` before it resolves. */
const appendScript = (src: string): HTMLScriptElement => {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = src;
  document.body.appendChild(script);
  return script;
};

/* --- Idle reload -------------------------------------------------------- */

const IDLE_THRESHOLD_MS = 1000 * 60 * 60 * 8;

/** Reloads a tab after eight hours with no click or keypress. */
export const startIdleReload = (): void => {
  let idleTimeout = 0;

  const scheduleReload = () => {
    idleTimeout = window.setTimeout(
      () => window.location.reload(),
      IDLE_THRESHOLD_MS
    );
  };

  const resetIdleTimer = () => {
    window.clearTimeout(idleTimeout);
    scheduleReload();
  };

  scheduleReload();
  window.addEventListener('click', resetIdleTimer, false);
  window.addEventListener('keypress', resetIdleTimer, false);
};

/* --- Intercom ----------------------------------------------------------- */

const INTERCOM_WORKSPACE_ID = process.env.REACT_APP_INTERCOM_WORKSPACE ?? '';
const REGION = process.env.REACT_APP_REGION ?? '';
const INTERCOM_WIDGET_DELAY_MS = 4000;

/** US traffic uses Intercom's default host; other regions are namespaced. */
const getIntercomApiBase = (): string =>
  REGION === 'us'
    ? 'https://api-iam.intercom.io'
    : `https://api-iam.${REGION}.intercom.io`;

/**
 * Deferred everywhere except a URL ending in `/us`, which loads immediately.
 * Carried over verbatim from the original inline loader; the reason for the US
 * exception is undocumented.
 */
const getIntercomDelayMs = (): number =>
  window.location.href.endsWith('/us') ? 0 : INTERCOM_WIDGET_DELAY_MS;

const installIntercomQueueStub = (): void => {
  const stub = function intercom(...args: unknown[]) {
    stub.c(args);
  } as IntercomStub;
  stub.q = [];
  stub.c = (args: unknown) => {
    stub.q.push(args as unknown[]);
  };

  getBootstrapWindow().Intercom = stub;
};

/**
 * Three outcomes, matching the original inline loader exactly: no stored session
 * boots anonymously, a stored session boots with identity, and a stored session
 * carrying no user id does not boot at all. `getUserData` reads localStorage
 * before sessionStorage where the inline version read them the other way round —
 * only distinguishable if both hold different users.
 */
const bootIntercom = (): void => {
  const intercom = getBootstrapWindow().Intercom;
  if (!intercom) return;

  const userData = getUserData();
  if (!userData) {
    intercom('boot', { app_id: INTERCOM_WORKSPACE_ID });
    return;
  }

  const user = userData.user;
  if (!user?.id) return;

  intercom('boot', {
    app_id: INTERCOM_WORKSPACE_ID,
    email: user.email,
    user_id: user.id,
    user_hash: user.intercomHash,
    created_at: Date.now(),
  });
};

const loadIntercomWidget = (): void => {
  window.setTimeout(() => {
    const script = appendScript(
      `https://widget.intercom.io/widget/${INTERCOM_WORKSPACE_ID}`
    );
    script.onload = bootIntercom;
  }, getIntercomDelayMs());
};

export const startIntercom = (): void => {
  const settings = {
    app_id: INTERCOM_WORKSPACE_ID,
    api_base: getIntercomApiBase(),
    custom_launcher_selector: `.${INTERCOM_LAUNCH_TARGET_CLASS}`,
  };
  getBootstrapWindow().intercomSettings = settings;

  /* A widget already on the page (a soft navigation, or a second call) only
     needs re-pointing at the current settings. */
  const existing = getBootstrapWindow().Intercom;
  if (typeof existing === 'function') {
    existing('reattach_activator');
    existing('update', settings);
    return;
  }

  installIntercomQueueStub();

  if (document.readyState === 'complete') {
    loadIntercomWidget();
    return;
  }
  window.addEventListener('load', loadIntercomWidget, false);
};

/* --- Google Tag Manager ------------------------------------------------- */

const GTM_ID = process.env.REACT_APP_GTM_ID ?? '';
const GTM_AUTH = process.env.REACT_APP_GTM_AUTH ?? '';

/** Analytics is production-only; every dev env is deliberately excluded. */
const GTM_PRODUCTION_HOST = 'www.inquisitive.com';
const GTM_STAGING_HOST = 'master.inquisitive.com';
const GTM_ENABLED_HOSTS = [GTM_PRODUCTION_HOST, GTM_STAGING_HOST];

/** Pages where analytics matters enough to load before any interaction. */
const GTM_EAGER_PATH_FRAGMENTS = ['trial', 'join'];

const isCypressRun = (): boolean =>
  !!document.cookie && document.cookie.indexOf('cypress=cypress') !== -1;

const isTagManagerEnabled = (): boolean =>
  !isCypressRun() && GTM_ENABLED_HOSTS.includes(window.location.host);

/** master.inquisitive.com drives GTM's staging container rather than live. */
const getTagManagerEnvironmentTag = (): string =>
  window.location.host === GTM_STAGING_HOST
    ? `&gtm_auth=${GTM_AUTH}&gtm_preview=env-37&gtm_cookies_win=x`
    : '';

export const initTagManager = (): void => {
  if (!isTagManagerEnabled()) {
    console.info('GTM is not enabled for this environment/device.');
    return;
  }

  const win = getBootstrapWindow();
  const dataLayer = win.dataLayer || [];
  win.dataLayer = dataLayer;

  const userId = getUserData()?.user?.id;
  if (userId) {
    dataLayer.push({ userId });
  }

  console.info('Load GTM');
  dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  appendScript(
    `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}${getTagManagerEnvironmentTag()}`
  );
};

export const startGoogleTagManager = (): void => {
  const loadOnce = () => {
    document.removeEventListener('click', loadOnce, false);
    document.removeEventListener('scroll', loadOnce, false);
    initTagManager();
  };

  const isEagerPath = GTM_EAGER_PATH_FRAGMENTS.some(fragment =>
    window.location.pathname.includes(fragment)
  );

  if (isEagerPath) {
    initTagManager();
    return;
  }

  document.addEventListener('click', loadOnce, false);
  document.addEventListener('scroll', loadOnce, false);
};

/* --- Crazy Egg ---------------------------------------------------------- */

const CRAZY_EGG_ENABLED_HOST = 'www.inquisitive.com';
const CRAZY_EGG_SCRIPT =
  'https://script.crazyegg.com/pages/scripts/0111/2868.js';

export const startCrazyEgg = (): void => {
  if (window.location.host !== CRAZY_EGG_ENABLED_HOST) return;

  console.info('Load Crazy Egg');
  appendScript(CRAZY_EGG_SCRIPT);
};

/* ------------------------------------------------------------------------ */

export const startBootstrap = (): void => {
  startIdleReload();
  startIntercom();
  startGoogleTagManager();
  startCrazyEgg();
};
